import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import { fetchPollDetails, voteOnPoll, removeVote, getUserVotes } from '../../services/pollService';
import moment from 'moment';
import Avatar from '../../Components/Avatar';

import defaultUserImg from '../../../assets/images/defaultuser.png';

const PollDetails = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { userData } = useAuth();
  
  const [poll, setPoll] = useState(null);
  const [userVotes, setUserVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  const { pollId } = route.params;

  useEffect(() => {
    loadPollDetails();
  }, [pollId]);

  const loadPollDetails = async () => {
    try {
      const [detailsResult, votesResult] = await Promise.all([
        fetchPollDetails(pollId),
        getUserVotes(pollId, userData?.id)
      ]);

      if (detailsResult.success) {
        setPoll(detailsResult.data);
      }

      if (votesResult.success) {
        setUserVotes(votesResult.data);
      }
    } catch (error) {
      Alert.alert('Error', 'Could not load poll details');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId) => {
    if (voting || !poll) return;
    
    setVoting(true);
    try {
      const isCurrentlyVoted = userVotes.includes(optionId);
      
      let result;
      if (isCurrentlyVoted) {
        // Remove vote
        result = await removeVote(poll.id, optionId, userData?.id);
        if (result.success) {
          setUserVotes(prev => prev.filter(id => id !== optionId));
        }
      } else {
        // Add vote
        const optionIds = poll.allow_multiple_choices 
          ? [...userVotes, optionId]
          : [optionId];
        
        result = await voteOnPoll(poll.id, optionIds, userData?.id);
        if (result.success) {
          setUserVotes(optionIds);
        }
      }

      if (result.success) {
        // Reload poll data to get updated results
        loadPollDetails();
      } else {
        Alert.alert('Error', result.msg);
      }
    } catch (error) {
      Alert.alert('Error', 'Could not vote');
    } finally {
      setVoting(false);
    }
  };

  const getTotalVotes = () => {
    return poll?.results?.reduce((total, result) => total + parseInt(result.vote_count), 0) || 0;
  };

  const getVotePercentage = (optionId) => {
    const result = poll?.results?.find(r => r.option_id === optionId);
    return result ? Math.round(parseFloat(result.percentage)) : 0;
  };

  const getOptionVoteCount = (optionId) => {
    const result = poll?.results?.find(r => r.option_id === optionId);
    return result ? parseInt(result.vote_count) : 0;
  };

  const isExpired = () => {
    return poll?.expires_at && moment(poll.expires_at).isBefore(moment());
  };

  if (loading) {
    return (
      <ScreenWrapper>
        <Header title="Poll Details" showBackButton />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading poll...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  if (!poll) {
    return (
      <ScreenWrapper>
        <Header title="Poll Details" showBackButton />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Poll not found</Text>
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <Header title="Poll Details" showBackButton />
      
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Poll Header */}
        <View style={styles.header}>
          <View style={styles.userInfo}>
            <Avatar
              source={poll.user?.image ? { uri: poll.user.image } : defaultUserImg}
              size={50}
            />
            <View style={styles.userDetails}>
              <Text style={styles.userName}>{poll.user?.name || 'Unknown User'}</Text>
              <Text style={styles.time}>
                {moment(poll.created_at).fromNow()}
                {poll.community && ` • ${poll.community.name}`}
              </Text>
            </View>
          </View>
        </View>

        {/* Poll Question */}
        <View style={styles.questionContainer}>
          <Text style={styles.question}>{poll.question}</Text>
          {poll.description && (
            <Text style={styles.description}>{poll.description}</Text>
          )}
        </View>

        {/* Total Votes - Show at top */}
        <View style={styles.totalVotesContainer}>
          <Text style={styles.totalVotesText}>
            Total Votes: {getTotalVotes()}
          </Text>
        </View>

        {/* Poll Options */}
        <View style={styles.optionsContainer}>
          {poll.poll_options?.map((option, index) => {
            const isVoted = userVotes.includes(option.id);
            const percentage = getVotePercentage(option.id);
            const optionVotes = getOptionVoteCount(option.id);
            
            return (
              <TouchableOpacity
                key={option.id}
                style={[
                  styles.optionButton,
                  isVoted && styles.optionVoted,
                  isExpired() && styles.optionDisabled
                ]}
                onPress={() => !isExpired() && handleVote(option.id)}
                disabled={isExpired() || voting}
              >
                <View style={styles.optionContent}>
                  <View style={[
                    styles.percentageBar,
                    { width: `${percentage}%` }
                  ]} />
                  
                  <View style={styles.optionTextContainer}>
                    <Text style={[
                      styles.optionText,
                      isVoted && styles.optionTextVoted,
                      isExpired() && styles.optionTextDisabled
                    ]}>
                      {option.option_text}
                    </Text>
                    
                    <Text style={styles.optionStats}>
                      {percentage}% • {optionVotes} {optionVotes !== 1 ? 'votes' : 'vote'}
                    </Text>
                  </View>
                  
                  {isVoted && (
                    <View style={styles.checkmark}>
                      <Text style={styles.checkmarkText}>✓</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Poll Stats */}
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{getTotalVotes()}</Text>
            <Text style={styles.statLabel}>
              Vote{getTotalVotes() !== 1 ? 's' : ''}
            </Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{poll.poll_options?.length || 0}</Text>
            <Text style={styles.statLabel}>Options</Text>
          </View>
          
          {poll.expires_at && (
            <View style={styles.statItem}>
              <Text style={[
                styles.statValue,
                isExpired() && styles.statValueExpired
              ]}>
                {isExpired() ? 'Expired' : 'Active'}
              </Text>
              <Text style={styles.statLabel}>
                {isExpired() 
                  ? moment(poll.expires_at).fromNow()
                  : `Expires ${moment(poll.expires_at).fromNow()}`
                }
              </Text>
            </View>
          )}
        </View>

        {/* Poll Settings */}
        <View style={styles.settingsContainer}>
          {poll.allow_multiple_choices && (
            <View style={styles.settingItem}>
              <Text style={styles.settingIcon}>✓</Text>
              <Text style={styles.settingText}>Multiple choice allowed</Text>
            </View>
          )}
          
          {poll.is_anonymous && (
            <View style={styles.settingItem}>
              <Text style={styles.settingIcon}>🔒</Text>
              <Text style={styles.settingText}>Anonymous poll</Text>
            </View>
          )}
          
          {poll.is_announcement && (
            <View style={styles.settingItem}>
              <Text style={styles.settingIcon}>📌</Text>
              <Text style={styles.settingText}>Community announcement</Text>
            </View>
          )}
        </View>
      </ScrollView>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: hp(1.6),
    color: theme.colors.gray,
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: hp(1.8),
    color: theme.colors.gray,
  },
  header: {
    marginBottom: 20,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.dark,
  },
  time: {
    fontSize: hp(1.4),
    color: theme.colors.gray,
    marginTop: 2,
  },
  questionContainer: {
    marginBottom: 24,
  },
  question: {
    fontSize: hp(2.2),
    fontWeight: '600',
    color: theme.colors.dark,
    lineHeight: 28,
    marginBottom: 8,
  },
  description: {
    fontSize: hp(1.6),
    color: theme.colors.gray,
    lineHeight: 22,
  },
  totalVotesContainer: {
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.primaryLight,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.primary,
  },
  totalVotesText: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.primary,
  },
  optionsContainer: {
    marginBottom: 24,
  },
  optionButton: {
    marginBottom: 12,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.colors.lightGray,
  },
  optionVoted: {
    backgroundColor: theme.colors.primaryLight,
    borderWidth: 2,
    borderColor: theme.colors.primary,
  },
  optionDisabled: {
    opacity: 0.6,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    position: 'relative',
  },
  percentageBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: theme.colors.primary,
    opacity: 0.1,
  },
  optionTextContainer: {
    flex: 1,
    zIndex: 1,
  },
  optionText: {
    fontSize: hp(1.8),
    color: theme.colors.dark,
    fontWeight: '700',
    marginBottom: 4,
  },
  optionTextVoted: {
    color: theme.colors.primary,
    fontWeight: '700',
  },
  optionTextDisabled: {
    color: theme.colors.gray,
    fontWeight: '700',
  },
  optionStats: {
    fontSize: hp(1.4),
    color: theme.colors.gray,
  },
  checkmark: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 12,
  },
  checkmarkText: {
    color: theme.colors.white,
    fontSize: hp(1.6),
    fontWeight: 'bold',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: theme.colors.lightGray,
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: hp(2.4),
    fontWeight: '700',
    color: theme.colors.dark,
  },
  statValueExpired: {
    color: theme.colors.red,
  },
  statLabel: {
    fontSize: hp(1.4),
    color: theme.colors.gray,
    marginTop: 4,
  },
  settingsContainer: {
    backgroundColor: theme.colors.lightGray,
    borderRadius: 12,
    padding: 16,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  settingIcon: {
    fontSize: hp(1.8),
    marginRight: 12,
  },
  settingText: {
    fontSize: hp(1.6),
    color: theme.colors.dark,
  },
});

export default PollDetails;
